import { useCompanyList, useForm } from 'app/hooks';
import { ICompanyCreate, ICompanySchema } from 'generated/axios';
import { List } from '../list';
import { useApi, useMessage } from 'app/context';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton } from '@mui/material';
import { t } from 'i18next';
import { buildErrorDetails, buildErrorMessage } from 'app/utils';
import { Typography } from '../typography';
import { TextField } from '../textfield';

//icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useState } from 'react';
import { Button } from '..';

interface Props {
  allowCreate?: boolean;
  onConfirmCompany: (company: ICompanySchema) => void;
  onCancel: () => void;
}

export const SearchCompany = ({ onConfirmCompany, onCancel, allowCreate }: Props) => {
  const { companyApi } = useApi();
  const { success, error } = useMessage();

  const {
    list,
    refresh,
    page,
    pageSize,
    handlePageChange,
    handleFilter,
    handlePageSizeChange,
    loading,
    setLoading,
    count,
  } = useCompanyList();

  const [search, setSearch] = useState<string>();

  const handleSearch = (newSearch?: string) => {
    setSearch(newSearch);
  };

  const create = (form: ICompanyCreate) => {
    setLoading(true);
    companyApi
      .webCreateCompany(form)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('company.createSuccess') as string });
        onConfirmCompany(resp.data);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
        setErrors(buildErrorDetails(err));
      })
      .finally(() => setLoading(false));
  };

  const { bindTextField, setErrors, submit } = useForm<ICompanyCreate>({
    onSubmit: create,
  });

  return (
    <>
      <Box bgcolor={'primary.main'} padding={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'inherit'} alignItems={'inherit'}>
          <Typography color={'common.white'} variant="h6">
            {t('company.select')}
          </Typography>
          <IconButton onClick={refresh}>
            <RefreshIcon sx={{ color: 'common.white' }} />
          </IconButton>
        </Box>

        <IconButton onClick={onCancel}>
          <ClearIcon sx={{ color: 'common.white' }} />
        </IconButton>
      </Box>
      <Grid container spacing={2} paddingBottom={2} display={'flex'} alignItems={'center'} padding={2}>
        <Grid size={4}>
          <TextField
            value={search}
            size="small"
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleFilter(search);
                e.preventDefault();
              }
            }}
          />
        </Grid>
        <Grid size={4}>
          <Button
            onClick={() => {
              handleFilter(search);
            }}
          >
            {t('actions.search')}
          </Button>
        </Grid>
      </Grid>
      <List
        list={list}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        count={count}
        keyGetter={(el) => el.id}
        onItemClick={onConfirmCompany}
        fields={[
          {
            title: t('company.name'),
            key: 'name',
          },
        ]}
      />
      {allowCreate && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('company.create')}</AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={7}>
                <TextField label={t('company.name')} {...bindTextField({ field: 'name' })} />
              </Grid>
              <Grid size={5} alignSelf={'center'}>
                <Button variant="contained" size="large" loading={loading} onClick={submit}>
                  {t('actions.create')}
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
